import { useState, useEffect } from 'react';
import { Layout } from '../components/layout/Layout';
import { useAuth } from '../contexts/AuthContext';
import { productsAPI, inventoryAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from '../components/ui/table';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from '../components/ui/select';
import { toast } from 'sonner';
import { Search, Plus, Minus, Package, History, AlertTriangle, Loader2 } from 'lucide-react';

export default function Inventory() {
    const { user } = useAuth();
    const [products, setProducts] = useState([]);
    const [lowStock, setLowStock] = useState([]);
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [adjustDialogOpen, setAdjustDialogOpen] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [adjustment, setAdjustment] = useState({ type: 'add', quantity: '', reason: '' });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (user?.store_id) fetchData();
        else setLoading(false);
    }, [user]);

    const fetchData = async () => {
        try {
            const [productsRes, lowStockRes, historyRes] = await Promise.all([
                productsAPI.getAll(),
                inventoryAPI.getLowStock(),
                inventoryAPI.getHistory()
            ]);
            setProducts(productsRes.data || []);
            setLowStock(lowStockRes.data || []);
            setHistory(historyRes.data || []);
        } catch (error) {
            console.error('Inventory fetch error:', error);
            // Show more specific error message
            const errorMsg = error.response?.data?.detail || error.message || 'Failed to load inventory data';
            toast.error(errorMsg);
            // Still set empty arrays so UI doesn't break
            setProducts([]);
            setLowStock([]);
            setHistory([]);
        } finally {
            setLoading(false);
        }
    };

    const filteredProducts = products.filter(p =>
        p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.sku.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const handleOpenAdjust = (product) => {
        setSelectedProduct(product);
        setAdjustment({ type: 'add', quantity: '', reason: '' });
        setAdjustDialogOpen(true);
    };

    const handleAdjustStock = async () => {
        if (!adjustment.quantity || !adjustment.reason) {
            toast.error('Please fill all fields');
            return;
        }

        const qty = parseInt(adjustment.quantity);
        if (qty <= 0) {
            toast.error('Quantity must be greater than 0');
            return;
        }

        setSaving(true);
        try {
            const adjustmentValue = adjustment.type === 'add' ? qty : -qty;
            
            if (adjustment.type === 'subtract' && selectedProduct.stock_quantity < qty) {
                toast.error('Cannot subtract more than current stock');
                setSaving(false);
                return;
            }

            await inventoryAPI.adjust({
                product_id: selectedProduct.id,
                adjustment: adjustmentValue,
                reason: adjustment.reason
            });

            toast.success('Stock adjusted successfully');
            setAdjustDialogOpen(false);
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to adjust stock');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Layout title="Inventory">
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
                </div>
            </Layout>
        );
    }

    return (
        <Layout title="Inventory">
            <div className="space-y-6" data-testid="inventory-page">
                {/* Low Stock Alert */}
                {lowStock.length > 0 && (
                    <Card className="border-amber-500/50">
                        <CardHeader className="pb-3">
                            <CardTitle className="text-lg flex items-center gap-2 text-amber-600">
                                <AlertTriangle className="h-5 w-5" />
                                Low Stock Alert ({lowStock.length} items)
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="flex flex-wrap gap-2">
                                {lowStock.map((product) => (
                                    <Badge 
                                        key={product.id} 
                                        variant="outline" 
                                        className="cursor-pointer hover:bg-amber-500/10"
                                        onClick={() => handleOpenAdjust(product)}
                                        data-testid={`low-stock-badge-${product.id}`}
                                    >
                                        {product.name}: {product.stock_quantity} left
                                    </Badge>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                )}

                <Tabs defaultValue="stock" className="space-y-4">
                    <TabsList>
                        <TabsTrigger value="stock" data-testid="stock-tab">
                            <Package className="h-4 w-4 mr-2" />
                            Stock Levels
                        </TabsTrigger>
                        <TabsTrigger value="history" data-testid="history-tab">
                            <History className="h-4 w-4 mr-2" />
                            History
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="stock" className="space-y-4">
                        <div className="relative max-w-md">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                                placeholder="Search products..."
                                className="pl-9"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                data-testid="inventory-search"
                            />
                        </div>

                        <Card>
                            <CardContent className="p-0">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Product</TableHead>
                                            <TableHead>SKU</TableHead>
                                            <TableHead>Category</TableHead>
                                            <TableHead className="text-right">Current Stock</TableHead>
                                            <TableHead className="text-right">Threshold</TableHead>
                                            <TableHead className="text-right">Status</TableHead>
                                            <TableHead className="w-[100px]"></TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {filteredProducts.map((product) => (
                                            <TableRow key={product.id} data-testid={`inventory-row-${product.id}`}>
                                                <TableCell>
                                                    <div className="flex items-center gap-3">
                                                        {product.image_url ? (
                                                            <img src={product.image_url} alt="" className="h-8 w-8 rounded object-cover" />
                                                        ) : (
                                                            <div className="h-8 w-8 rounded bg-secondary flex items-center justify-center">
                                                                <Package className="h-4 w-4 text-muted-foreground" />
                                                            </div>
                                                        )}
                                                        <span className="font-medium">{product.name}</span>
                                                    </div>
                                                </TableCell>
                                                <TableCell className="font-mono text-sm">{product.sku}</TableCell>
                                                <TableCell>{product.category_name}</TableCell>
                                                <TableCell className="text-right font-mono font-bold">
                                                    {product.stock_quantity}
                                                </TableCell>
                                                <TableCell className="text-right text-muted-foreground">
                                                    {product.low_stock_threshold}
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    <Badge variant={product.stock_quantity <= product.low_stock_threshold ? "destructive" : product.stock_quantity <= product.low_stock_threshold * 2 ? "secondary" : "default"}>
                                                        {product.stock_quantity <= product.low_stock_threshold ? "Low" : product.stock_quantity <= product.low_stock_threshold * 2 ? "Medium" : "Good"}
                                                    </Badge>
                                                </TableCell>
                                                <TableCell>
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => handleOpenAdjust(product)}
                                                        data-testid={`adjust-stock-${product.id}`}
                                                    >
                                                        Adjust
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    <TabsContent value="history">
                        <Card>
                            <CardContent className="p-0">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Date</TableHead>
                                            <TableHead>Product</TableHead>
                                            <TableHead className="text-right">Adjustment</TableHead>
                                            <TableHead>Reason</TableHead>
                                            <TableHead>By</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {history.map((entry) => (
                                            <TableRow key={entry.id} data-testid={`history-row-${entry.id}`}>
                                                <TableCell className="text-muted-foreground">
                                                    {new Date(entry.created_at).toLocaleString()}
                                                </TableCell>
                                                <TableCell>{products.find(p => p.id === entry.product_id)?.name || 'Unknown'}</TableCell>
                                                <TableCell className="text-right">
                                                    <span className={entry.adjustment > 0 ? "text-emerald-600" : "text-red-600"}>
                                                        {entry.adjustment > 0 ? '+' : ''}{entry.adjustment}
                                                    </span>
                                                </TableCell>
                                                <TableCell>{entry.reason}</TableCell>
                                                <TableCell className="text-muted-foreground">{entry.user_name}</TableCell>
                                            </TableRow>
                                        ))}
                                        {history.length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                                                    No history records
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </CardContent>
                        </Card>
                    </TabsContent>
                </Tabs>
            </div>

            {/* Adjust Stock Dialog */}
            <Dialog open={adjustDialogOpen} onOpenChange={setAdjustDialogOpen}>
                <DialogContent data-testid="adjust-dialog">
                    <DialogHeader>
                        <DialogTitle>Adjust Stock</DialogTitle>
                        <DialogDescription>
                            {selectedProduct?.name} - Current stock: <span className="font-mono font-bold">{selectedProduct?.stock_quantity}</span>
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label>Adjustment Type</Label>
                            <Select value={adjustment.type} onValueChange={(v) => setAdjustment({ ...adjustment, type: v })}>
                                <SelectTrigger data-testid="adjustment-type-select">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="add">
                                        <span className="flex items-center gap-2">
                                            <Plus className="h-4 w-4 text-emerald-600" /> Add Stock
                                        </span>
                                    </SelectItem>
                                    <SelectItem value="subtract">
                                        <span className="flex items-center gap-2">
                                            <Minus className="h-4 w-4 text-red-600" /> Remove Stock
                                        </span>
                                    </SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="qty">Quantity</Label>
                            <Input
                                id="qty"
                                type="number"
                                min="1"
                                value={adjustment.quantity}
                                onChange={(e) => setAdjustment({ ...adjustment, quantity: e.target.value })}
                                data-testid="adjustment-quantity-input"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="reason">Reason</Label>
                            <Input
                                id="reason"
                                value={adjustment.reason}
                                onChange={(e) => setAdjustment({ ...adjustment, reason: e.target.value })}
                                placeholder="e.g., Received shipment, Damaged goods, Count correction"
                                data-testid="adjustment-reason-input"
                            />
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setAdjustDialogOpen(false)}>Cancel</Button>
                        <Button onClick={handleAdjustStock} disabled={saving} data-testid="save-adjustment-btn">
                            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            Save Adjustment
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Layout>
    );
}
